import React, { useState, useEffect } from 'react';
import { tasksApi } from '../../api/tasks';
import { adminApi } from '../../api/admin';
import toast from 'react-hot-toast';
import TaskDetailModal from './TaskDetailModal';
import CreateTaskModal from './CreateTaskModal';
import { CheckCircle2, Circle, Plus, ChevronDown, ChevronRight, Clock, AlertTriangle, User, Trash2, Pause, Play, Send, MessageSquare, ExternalLink } from 'lucide-react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const CATEGORY_LABELS = {
  general: 'General', admin: 'Administrative', medical: 'Medical', legal: 'Legal',
  investigation: 'Investigation', client_comm: 'Client Comm', discovery: 'Discovery', settlement: 'Settlement',
};

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const now = new Date();
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: Math.abs(diffDays) + 'd overdue', className: 'text-red-600 font-medium' };
  if (diffDays === 0) return { text: 'Due today', className: 'text-orange-600 font-medium' };
  if (diffDays === 1) return { text: 'Due tomorrow', className: 'text-orange-500' };
  if (diffDays <= 7) return { text: 'Due in ' + diffDays + 'd', className: 'text-yellow-600' };
  return { text: due.toLocaleDateString(), className: 'text-gray-500' };
}

function TaskRow({ task, allTasks, users, onComplete, onReopen, onDelete, onStatusChange, onAssign, onRefresh, onSelect, onJumpToTab }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const isCompleted = task.status === 'completed';
  const isOnHold = task.status === 'on_hold';
  const isBlocked = task.status === 'blocked';
  const due = formatDueDate(task.due_date);
  const isOverdue = due && due.className.includes('red') && !isCompleted;

  const incompleteDeps = (task.depends_on || []).map(depId => {
    const dep = (allTasks || []).find(t => t.id === depId);
    return dep && dep.status !== 'completed' ? dep.title : null;
  }).filter(Boolean);
  const hasBlockers = incompleteDeps.length > 0 && !isCompleted;

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const res = await tasksApi.getComments(task.id);
      setComments(res.data.comments);
    } catch (e) {}
    finally { setLoadingComments(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await tasksApi.addComment(task.id, newComment);
      setComments([...comments, res.data.comment]);
      setNewComment('');
    } catch (e) { toast.error('Failed to add comment'); }
  };

  const toggleExpand = () => {
    if (onSelect) { onSelect(task); return; }
    if (!expanded) fetchComments();
    setExpanded(!expanded);
  };

  const statusIcon = () => {
    if (isOnHold) return <Pause className="h-5 w-5 text-yellow-500" />;
    if (isBlocked) return <AlertTriangle className="h-5 w-5 text-red-400" />;
    if (isCompleted) return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    return <Circle className={'h-5 w-5 ' + (isOverdue ? 'text-red-400' : hasBlockers ? 'text-amber-400' : 'text-gray-300 hover:text-brand-500')} />;
  };

  return (
    <div className={'border-b border-gray-50 last:border-0 ' + (isCompleted ? 'opacity-60' : '')}>
      <div className="flex items-start gap-3 px-4 py-2.5 group hover:bg-gray-50">
        <button onClick={() => isCompleted ? onReopen(task.id) : isOnHold ? onStatusChange(task.id, 'pending') : onComplete(task.id)} className="mt-0.5 flex-shrink-0">
          {statusIcon()}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={toggleExpand}>
          <div className="flex items-center gap-2 flex-wrap">
            {task.task_number && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">T-{String(task.task_number).padStart(4, '0')}</span>}
            <span className={'text-sm ' + (isCompleted ? 'line-through text-gray-400' : 'text-gray-900')}>{task.title}</span>
            {task.auto_generated && <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500">auto</span>}
            <span className={'text-[10px] px-1.5 py-0.5 rounded ' + (PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal)}>{task.priority}</span>
            {isOnHold && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">on hold</span>}
            {isBlocked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">blocked</span>}
          </div>
          {hasBlockers && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md w-fit">
              <AlertTriangle className="h-3 w-3" />
              <span>Complete first: {incompleteDeps.join(', ')}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {due && <span className={'text-[10px] flex items-center gap-1 ' + due.className}><Clock className="h-3 w-3" />{due.text}</span>}
            {task.assigned_first_name && (
              <span className="text-[10px] text-gray-400 flex items-center gap-1"><User className="h-3 w-3" />{task.assigned_first_name} {task.assigned_last_name}</span>
            )}
            {task.category && task.category !== 'general' && (
              <span className="text-[10px] text-gray-400">{CATEGORY_LABELS[task.category] || task.category}</span>
            )}
            {task.screen_id && onJumpToTab && (
              <button onClick={(e) => { e.stopPropagation(); onJumpToTab(task.screen_id); }}
                className="text-[10px] text-brand-500 hover:text-brand-700 flex items-center gap-0.5" title="Go to linked tab">
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {!isCompleted && !isOnHold && (
            <button onClick={() => onStatusChange(task.id, 'on_hold')} className="p-1 text-gray-400 hover:text-yellow-600" title="Put on hold"><Pause className="h-3.5 w-3.5" /></button>
          )}
          {isOnHold && (
            <button onClick={() => onStatusChange(task.id, 'pending')} className="p-1 text-gray-400 hover:text-green-600" title="Resume"><Play className="h-3.5 w-3.5" /></button>
          )}
          <button onClick={() => onDelete(task.id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3 ml-8 space-y-3 bg-gray-50/50">
          <div className="flex items-center gap-2 pt-2">
            <span className="text-xs text-gray-500 w-16">Assign to:</span>
            <select value={task.assigned_to || ''} onChange={(e) => onAssign(task.id, e.target.value || null)}
              className="text-xs px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500">
              <option value="">Unassigned</option>
              {(users || []).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16">Status:</span>
            <div className="flex gap-1">
              {['pending', 'in_progress', 'on_hold', 'blocked'].map(s => (
                <button key={s} onClick={() => onStatusChange(task.id, s)}
                  className={'text-[10px] px-2 py-1 rounded-lg border transition-colors ' +
                    (task.status === s ? 'bg-brand-50 border-brand-300 text-brand-700 font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500 font-medium">Comments</span>
            {loadingComments ? <p className="text-[10px] text-gray-400 mt-1">Loading...</p> : (
              <div className="mt-1 space-y-1.5">
                {comments.map(c => (
                  <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-gray-700">{c.first_name} {c.last_name}</span>
                      <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{c.content}</p>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-[10px] text-gray-400 mt-1">No comments yet</p>}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <input type="text" value={newComment} onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddComment(); }}
                placeholder="Add a comment..."
                className="flex-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white" />
              <button onClick={handleAddComment} disabled={!newComment.trim()}
                className="p-1.5 text-brand-600 hover:text-brand-700 disabled:opacity-30">
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaskPanel({ recordId, recordTypeId, record, onJumpToTab, tabs }) {
  const [tasks, setTasks] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [users, setUsers] = useState([]);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'normal', category: 'general', assigned_to: '', due_date: '', depends_on: [], workflow_stage_id: '' });
  const [filter, setFilter] = useState('all');
  const [stages, setStages] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);

  const fetchTasks = async () => {
    try {
      const res = await tasksApi.getForRecord(recordId);
      setTasks(res.data.tasks);
      setGrouped(res.data.grouped);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchTasks();
    adminApi.getUsers().then(res => setUsers(res.data.users || [])).catch(() => {});
    // Fetch workflow stages for the stage picker
    if (record?.workflow_id) {
      adminApi.getWorkflowStages(record.workflow_id)
        .then(res => setStages(res.data.stages || []))
        .catch(() => {});
    }
    if (record?.workflow_stage_id) {
      setNewTask(prev => ({ ...prev, workflow_stage_id: record.workflow_stage_id }));
    }
  }, [recordId, record?.workflow_id]);

  const handleComplete = async (taskId, force) => {
    try {
      await tasksApi.complete(taskId, force ? { force: true } : {});
      toast.success('Task completed');
      fetchTasks();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.warning) {
        if (window.confirm(err.response.data.message + '\n\nComplete anyway?')) {
          handleComplete(taskId, true);
        }
      } else {
        toast.error(err.response?.data?.error || 'Failed to complete task');
      }
    }
  };

  const handleReopen = async (taskId) => {
    try { await tasksApi.reopen(taskId); toast.success('Task reopened'); fetchTasks(); }
    catch (err) { toast.error('Failed to reopen task'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try { await tasksApi.delete(taskId); toast.success('Task deleted'); fetchTasks(); }
    catch (err) { toast.error('Failed to delete task'); }
  };

  const handleStatusChange = async (taskId, status) => {
    try { await tasksApi.setStatus(taskId, status); toast.success('Status updated'); fetchTasks(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to update status'); }
  };

  const handleAssign = async (taskId, userId) => {
    try { await tasksApi.update(taskId, { assigned_to: userId || null }); toast.success('Task assigned'); fetchTasks(); }
    catch (err) { toast.error('Failed to assign task'); }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    try {
      await tasksApi.create({ ...newTask, record_id: recordId, assigned_to: newTask.assigned_to || undefined, due_date: newTask.due_date || undefined, depends_on: newTask.depends_on.length > 0 ? newTask.depends_on : undefined, workflow_stage_id: newTask.workflow_stage_id || undefined });
      toast.success('Task added');
      setNewTask({ title: '', description: '', priority: 'normal', category: 'general', assigned_to: '', due_date: '', depends_on: [], workflow_stage_id: record?.workflow_stage_id || '' });
      setShowAdd(false);
      fetchTasks();
    } catch (err) { toast.error('Failed to add task'); }
  };

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;

  const filteredGrouped = {};
  Object.keys(grouped).forEach(cat => {
    const filtered = grouped[cat].filter(t => {
      if (filter === 'all') return true;
      if (filter === 'open') return t.status !== 'completed' && t.status !== 'cancelled';
      if (filter === 'overdue') return t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed';
      if (filter === 'on_hold') return t.status === 'on_hold';
      return true;
    });
    if (filtered.length > 0) filteredGrouped[cat] = filtered;
  });

  if (loading) return <div className="bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-400">Loading tasks...</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Tasks</h3>
          <span className="text-xs text-gray-500">{completedCount}/{totalCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </div>
      {/* Progress bar */}
      <div className="px-4 py-2 border-b border-gray-50">
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-200 rounded-full h-1.5">
            <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: totalCount ? (completedCount / totalCount * 100) + '%' : '0%' }} />
          </div>
          <span className="text-[10px] text-gray-500">{totalCount ? Math.round(completedCount / totalCount * 100) : 0}%</span>
        </div>
        {/* Filters */}
        <div className="flex gap-1 mt-2">
          {[{ id: 'all', label: 'All' }, { id: 'open', label: 'Open' }, { id: 'overdue', label: 'Overdue' }, { id: 'on_hold', label: 'On Hold' }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={'text-[10px] px-2 py-0.5 rounded-full transition-colors ' +
                (filter === f.id ? 'bg-brand-100 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-100')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
            {/* Task list by category */}
      <div>
        {Object.entries(filteredGrouped).map(([cat, catTasks]) => (
          <div key={cat}>
            <button onClick={() => setCollapsedCats(p => ({ ...p, [cat]: !p[cat] }))}
              className="flex items-center gap-2 w-full px-4 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 border-b border-gray-50">
              {collapsedCats[cat] ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {cat} ({catTasks.length})
            </button>
            {!collapsedCats[cat] && catTasks.map(task => (
              <TaskRow key={task.id} task={task} allTasks={tasks} users={users}
                onComplete={handleComplete} onReopen={handleReopen} onDelete={handleDelete}
                onStatusChange={handleStatusChange} onAssign={handleAssign} onRefresh={fetchTasks} onSelect={setSelectedTask} onJumpToTab={onJumpToTab} />
            ))}
          </div>
        ))}
        {Object.keys(filteredGrouped).length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">
            {filter === 'all' ? 'No tasks yet' : 'No ' + filter.replace('_', ' ') + ' tasks'}
          </p>
        )}
      </div>
      {selectedTask && (
        <TaskDetailModal task={selectedTask} allTasks={tasks}
          onClose={() => setSelectedTask(null)}
          onRefresh={() => { fetchTasks(); setSelectedTask(null); }}
          onJumpToTab={onJumpToTab} tabs={tabs} />
      )}
      {showCreateModal && (
        <CreateTaskModal
          recordId={recordId} record={record} tasks={tasks}
          users={users} stages={stages}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { fetchTasks(); setShowCreateModal(false); }} />
      )}
    </div>
  );
}
